import { useEffect, useState } from "react";
import { BellRing, Smartphone, Share } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { enablePush, disablePush, currentSubscription, pushSupported, isIOS, isStandalone } from "@/lib/push";

function PrefRow({ id, label, hint, checked, onChange }) {
  return (
    <label htmlFor={id} className="flex items-start justify-between gap-4 py-3 border-b border-border last:border-0 cursor-pointer">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
      </div>
      <Switch id={id} data-testid={id} checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

export function AlertSettingsDialog({ open, onOpenChange }) {
  const [prefs, setPrefs] = useState({ payments: true, overdue: true });
  const [onDevice, setOnDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const iosNeedsInstall = isIOS() && !isStandalone();

  useEffect(() => {
    if (!open) { return; }
    api.get("/me/notify-prefs").then((r) => setPrefs({ payments: r.data.payments, overdue: r.data.overdue })).catch(() => {});
    currentSubscription().then((s) => setOnDevice(!!s && Notification.permission === "granted")).catch(() => {});
  }, [open]);

  const savePref = async (key, v) => {
    const next = { ...prefs, [key]: v };
    setPrefs(next);
    try { await api.put("/me/notify-prefs", next); } catch { toast.error("Couldn't save preference"); setPrefs(prefs); }
  };

  const toggleDevice = async () => {
    setBusy(true);
    try {
      if (onDevice) { await disablePush(); setOnDevice(false); toast.success("Alerts turned off on this device"); }
      else {
        await enablePush(); setOnDevice(true);
        const r = await api.post("/push/test");
        toast.success(r.data.sent ? "Alerts on. A test alert is on its way." : "Alerts on for this device");
      }
    } catch (e) { toast.error(e?.message || "Couldn't change alerts"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="alert-settings-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="h-5 w-5 text-primary" />Phone alerts</DialogTitle>
          <DialogDescription>Get a notification on this device when money comes in or invoices slip overdue.</DialogDescription>
        </DialogHeader>
        {iosNeedsInstall ? (
          <div data-testid="alert-ios-hint" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
            <Share className="h-4 w-4 mt-0.5 shrink-0" />
            <span>On iPhone, tap <b>Share</b> then <b>Add to Home Screen</b>, open the app from your Home Screen, and come back here to turn alerts on.</span>
          </div>
        ) : (
          <div className="rounded-md border border-border p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Smartphone className="h-4 w-4 text-muted-foreground" />
              <span data-testid="alert-device-status">{onDevice ? "On for this device" : pushSupported() ? "Off on this device" : "Not supported in this browser"}</span>
            </div>
            <Button data-testid="alert-device-toggle" size="sm" variant={onDevice ? "outline" : "default"} disabled={busy || !pushSupported()} onClick={toggleDevice}>
              {busy ? "Working…" : onDevice ? "Turn off" : "Enable alerts"}
            </Button>
          </div>
        )}
        <div>
          <PrefRow id="pref-payments" label="Payment recorded" hint="When a teammate records a payment in your projects." checked={prefs.payments} onChange={(v) => savePref("payments", v)} />
          <PrefRow id="pref-overdue" label="Daily overdue summary" hint="9:00 AM IST, only on days when invoices became overdue." checked={prefs.overdue} onChange={(v) => savePref("overdue", v)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
